import { call, put, takeLatest, take, all, race } from 'redux-saga/effects'
import { eventChannel, END } from 'redux-saga'
import chatApi from 'api/ChatApi'
import { handleError } from 'api/api-utils'
import {
  sendChatMessageSuccess,
  sendChatMessageError,
  sendChatMessageStatus,
  sendChatMessageStreamChunk,
  loadConversationsSuccess,
  loadConversationsError,
  loadConversationSuccess,
  loadConversationError,
  deleteConversationSuccess,
  deleteConversationError,
  updateConversationTitle,
  clearChatHistory,
} from './actions'
import {
  SEND_CHAT_MESSAGE,
  LOAD_CONVERSATIONS,
  LOAD_CONVERSATION,
  DELETE_CONVERSATION,
  MESSAGE_KEY_TO_CODE,
  NETWORK_ERROR,
  RESET_CHAT_SCOPE,
} from './constants'
import { LOGOUT_SUCCESS, CHOOSE_PROFILE } from 'containers/App/constants'

// reducers.js reinitialises the whole store on logout and on a profile switch, the same way it does
// on a scope change, but none of the three actually cancels a running saga or aborts its in-flight
// request. A response that lands after one of these still writes into the freshly reset store — on
// a shared browser, session B would end up seeing session A's conversation. Racing against all three
// closes the channel / drops the response the same way a scope change already does.
export const SCOPE_ENDED = [RESET_CHAT_SCOPE, LOGOUT_SUCCESS, CHOOSE_PROFILE]

function createChatStreamChannel(message, conversationId, scopeCode, context) {
  return eventChannel((emitter) => {
    let result = {
      content: '',
      sources: [],
      suggestedQuestions: [],
      conversationId: null,
      conversationTitle: null,
      tokenResetDate: null,
      tokensUsed: 0,
      timestamp: null,
    }

    const abort = chatApi.sendMessage({
      message,
      conversationId,
      scopeCode,
      context,
      onStatus: (event) => {
        emitter({ type: 'status', data: event })
      },
      onContent: (event) => {
        result.content += event.content || ''
        result.timestamp = event.timestamp
        emitter({ type: 'chunk', data: event })
      },
      onSources: (event) => {
        result.sources = event.sources || []
      },
      onSuggestions: (event) => {
        result.suggestedQuestions = event.suggestedQuestions || []
      },
      onComplete: (event) => {
        result.tokensUsed = event.tokensUsed || 0
        result.conversationId = event.conversationId
        result.conversationTitle = event.conversationTitle
        result.tokenResetDate = event.tokenResetDate
        if (event.response != null || event.content != null) {
          result.content = event.response ?? event.content
        }

        emitter({
          type: 'complete',
          data: {
            response: result.content,
            conversationId: result.conversationId,
            conversationTitle: result.conversationTitle,
            tokenResetDate: result.tokenResetDate,
            sources: result.sources,
            suggestedQuestions: result.suggestedQuestions,
            tokensUsed: result.tokensUsed,
            truncated: event.truncated === true,
            timestamp: result.timestamp,
          },
        })
      },
      onTitle: (event) => {
        emitter({ type: 'title', data: event })
      },
      onStreamEnd: () => {
        emitter(END)
      },
      onError: (event) => {
        const messageKey = event.MessageKey || event.messageKey
        const errorCode =
          event.ErrorCode ||
          event.errorCode ||
          MESSAGE_KEY_TO_CODE[messageKey] ||
          null
        emitter({
          type: 'error',
          data: {
            code: errorCode,
            messageKey,
            message: event.Content || event.content,
            data: event.ErrorDetails || event.errorDetails,
            // Set when the stream failed after the question had already been stored. Adopting it
            // keeps a retry inside that same conversation instead of opening a second one and
            // orphaning the first, which still holds the question.
            conversationId:
              event.ConversationId ?? event.conversationId ?? null,
          },
        })
        emitter(END)
      },
    })

    return () => {
      if (abort) abort()
    }
  })
}

export function* sendChatMessageSaga(action) {
  const { message, conversationId, companyCode: scopeCode, context } = action

  try {
    const channel = yield call(
      createChatStreamChannel,
      message,
      conversationId,
      scopeCode,
      context
    )

    // The stream is only finished when the server says so. A body that just ends — a proxy timing
    // out, the API restarting, a truncated response — reaches onStreamEnd and closes the channel
    // without a complete or an error, and nothing was dispatched: isLoading stayed true and the empty
    // streaming bubble sat there forever, with no way to send another message.
    let receivedTerminalEvent = false
    let streamEndedWithoutResult = false

    try {
      while (true) {
        // Race the stream against a scope change, a logout or a profile switch. takeLatest only
        // cancels this saga when a NEW message is sent, so without this a company switch, logout or
        // profile switch mid-stream left the request running: its later chunks and success
        // repopulated the freshly reset store, putting the previous company's — or previous user's
        // — answer on screen under the new company/user. Breaking out runs the finally below, which
        // closes the channel and aborts the fetch.
        const { event, reset } = yield race({
          event: take(channel),
          reset: take(SCOPE_ENDED),
        })

        if (reset) break

        if (event === END) {
          streamEndedWithoutResult = !receivedTerminalEvent
          break
        }

        switch (event.type) {
          case 'status':
            yield put(
              sendChatMessageStatus(
                event.data.MessageKey ||
                  event.data.messageKey ||
                  event.data.Content ||
                  event.data.content
              )
            )
            break
          case 'chunk':
            yield put(sendChatMessageStreamChunk(event.data.content))
            break
          case 'complete':
            receivedTerminalEvent = true
            yield put(sendChatMessageSuccess(event.data))
            break
          case 'title':
            yield put(
              updateConversationTitle(
                event.data.conversationId,
                event.data.title
              )
            )
            break
          case 'error':
            receivedTerminalEvent = true
            yield put(sendChatMessageError(event.data))
            break
          default:
            break
        }
      }
    } finally {
      channel.close()
    }

    // Deliberately outside the finally: that also runs when takeLatest cancels this saga for a new
    // message, and on a scope change, neither of which is a failed stream.
    if (streamEndedWithoutResult) {
      yield put(sendChatMessageError({ code: NETWORK_ERROR }))
    }
  } catch (error) {
    yield put(
      handleError(
        error.data || { message: error.message },
        sendChatMessageError
      )
    )
  }
}

// Both loaders race their request against a scope change, a logout or a profile switch, for the
// same reason the stream does: takeLatest only cancels on another request of the same kind, never
// on a company switch, a logout or a profile switch, and it does not abort the axios call already
// on the wire. A response that lands after one of those writes the previous company's — or
// previous user's — data straight into the store the reset just emptied.
export function* loadConversationsSaga(action) {
  try {
    const { conversations, reset } = yield race({
      conversations: call(chatApi.getConversations, action.companyCode),
      reset: take(SCOPE_ENDED),
    })

    if (reset) return

    yield put(loadConversationsSuccess(conversations))
  } catch (error) {
    yield put(loadConversationsError(error))
  }
}

export function* loadConversationSaga(action) {
  try {
    const { conversation, reset } = yield race({
      conversation: call(
        chatApi.getConversation,
        action.companyCode,
        action.conversationId
      ),
      reset: take(SCOPE_ENDED),
    })

    // The worst of the three: this one restores messages AND the conversation id, so a late
    // response puts the previous company's — or previous user's — exchange on screen under the
    // new company's/user's heading.
    if (reset) return

    yield put(loadConversationSuccess(conversation))
  } catch (error) {
    if (error.response?.status === 404) {
      yield put(clearChatHistory())
    } else {
      yield put(loadConversationError(error))
    }
  }
}

export function* deleteConversationSaga(action) {
  try {
    // Same race as the loaders: takeLatest does not abort the DELETE already on the wire, so without
    // this a logout, profile switch or scope change mid-request would still land its success or
    // error in the store the reset just emptied.
    const { reset } = yield race({
      deleted: call(
        chatApi.deleteConversation,
        action.companyCode,
        action.conversationId
      ),
      reset: take(SCOPE_ENDED),
    })

    if (reset) return

    yield put(deleteConversationSuccess(action.conversationId))
  } catch (error) {
    yield put(deleteConversationError(error))
  }
}

export default function* chatSaga() {
  yield all([
    takeLatest(SEND_CHAT_MESSAGE, sendChatMessageSaga),
    takeLatest(LOAD_CONVERSATIONS, loadConversationsSaga),
    takeLatest(LOAD_CONVERSATION, loadConversationSaga),
    takeLatest(DELETE_CONVERSATION, deleteConversationSaga),
  ])
}
